<template>
  <!-- Fixed Header -->
  <header
      class="fixed top-0 left-0 right-0 bg-card/95 backdrop-blur-sm shadow-lg dark:shadow-[0_6px_32px_rgba(0,0,0,0.4),0_1.5px_8px_rgba(80,120,255,0.3)] z-40 transition-all duration-500 border-b border-border/40 dark:border-border/60 rounded-b-2xl header-animate">
    <div class="container mx-auto px-4 h-16 flex items-center justify-between md:h-20">
      <!-- Left side: Theme Toggle -->
      <div>
        <Button
            variant="ghost"
            size="icon"
            @click="toggleTheme"
            class="rounded-full hover:bg-accent transition-all duration-300"
        >
          <Sun class="h-5 w-5 rotate-0 scale-100 transition-transform dark:-rotate-90 dark:scale-0"/>
          <Moon class="absolute h-5 w-5 rotate-90 scale-0 transition-transform dark:rotate-0 dark:scale-100"/>
          <span class="sr-only">Toggle theme</span>
        </Button>
      </div>

      <!-- Center: Logo -->
      <div class="absolute left-1/2 transform -translate-x-1/2 flex items-center justify-center">
        <router-link to="/">
          <img src="@/assets/img/logo-onlstreet.png" alt="Logo Onlstreet" class="h-10 md:h-12"/>
        </router-link>
      </div>

      <!-- Right side: Rules, User Profile and Logout -->
      <div class="flex items-center gap-2" v-if="!user">
        <RouterLink to="/the-le-chuong-trinh"
                    class="py-2 px-3 rounded-md flex items-center gap-1 hover:bg-primary/10 hover:text-primary transition-colors"
        >
          <FileText class="h-4 w-4"/>
          <span class="hidden sm:inline">Thể lệ</span>
        </RouterLink>

        <RouterLink to="/login"
                    class="py-2 px-3 rounded-md flex items-center gap-1 hover:bg-primary/10 hover:text-primary transition-colors"
        >
          <LogIn class="h-4 w-4"/>
          <span class="hidden sm:inline">Đăng nhập</span>
        </RouterLink>
      </div>

      <DropdownMenu v-on:update:open="handleShowHideProfileMenu()" v-else>
        <dropdown-menu-trigger
            as-child
            class="flex items-center gap-x-2 cursor-pointer h-9 px-2 py-1 rounded-md md:h-10 hover:bg-background"
        >
          <div>
            <avatar class="h-7 w-7 md:h-8 md:w-8">
              <avatar-fallback class="text-xs font-medium md:text-sm"
              >U
              </avatar-fallback
              >
            </avatar>
            <span class="font-medium hidden sm:block">{{ user?.Name }}</span>
            <svg
                :class="[
                '!w-3 !h-3 !transition-transform !duration-700',
                isShowProfileMenu ? 'rotate-180' : 'rotate-0'
              ]"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 10 6"
            >
              <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="m1 1 4 4 4-4"
              />
            </svg>
          </div>
        </dropdown-menu-trigger>
        <DropdownMenuContent class="w-56 menu-content">
          <DropdownMenuLabel>Tài khoản của tôi</DropdownMenuLabel>
          <DropdownMenuSeparator/>
          <DropdownMenuGroup>
            <DropdownMenuItem v-on:click="navigateToPage('/profile')">
              <User class="mr-2 h-4 w-4"/>
              <span>Hồ sơ</span>
              <DropdownMenuShortcut>⇧⌘P</DropdownMenuShortcut>
            </DropdownMenuItem>
            <DropdownMenuItem v-on:click="navigateToPage('/the-le-chuong-trinh')">
              <FileText class="mr-2 h-4 w-4"/>
              <span>Thể lệ chương trình</span>
            </DropdownMenuItem>
            <DropdownMenuItem disabled>
              <Settings class="mr-2 h-4 w-4"/>
              <span>Cài đặt</span>
              <DropdownMenuShortcut>⌘S</DropdownMenuShortcut>
            </DropdownMenuItem>
            <DropdownMenuItem v-on:click="handleClickOutside()">
              <LifeBuoy class="mr-2 h-4 w-4"/>
              <span>Hỗ trợ</span>
            </DropdownMenuItem>
          </DropdownMenuGroup>
          <DropdownMenuSeparator/>
          <DropdownMenuItem v-on:click="logout">
            <LogOut class="mr-2 h-4 w-4"/>
            <span>Đăng xuất</span>
            <DropdownMenuShortcut>⇧⌘Q</DropdownMenuShortcut>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  </header>
</template>
<script setup lang="ts">
import {Button} from "@/components/ui/button";
import {LogOut, Sun, Moon, FileText, LogIn} from "lucide-vue-next";
import {useColorMode} from "@vueuse/core";
import {useAuthStore} from '@/stores/auth';
import {computed, ref} from "vue";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  LifeBuoy,
  Settings,
  User
} from 'lucide-vue-next'
import {Avatar, AvatarFallback} from '@/components/ui/avatar'
import {useRouter} from "vue-router";

const mode = useColorMode();
const auth = useAuthStore();
const router = useRouter();

const user = computed(() => auth.user);
const isShowProfileMenu = ref(false)

function toggleTheme() {
  mode.value = mode.value === 'dark' ? 'light' : 'dark';
}

function logout() {
  auth.logout();
}

function handleShowHideProfileMenu() {
  isShowProfileMenu.value = !isShowProfileMenu.value
}

const navigateToPage = (path: string) => {
  router.push(path);
}

const handleClickOutside = () => {
  window.location.replace('https://zalo.me/3224497516475266551')
}
</script>

<style scoped>
.header-animate {
  animation: header-slide-in 0.6s cubic-bezier(0.22, 1, 0.36, 1);
  transform-origin: top center;
  will-change: transform, opacity, backdrop-filter;
}

@keyframes header-slide-in {
  0% {
    transform: translateY(-100%);
    opacity: 0;
    backdrop-filter: blur(0);
  }
  100% {
    transform: translateY(0);
    opacity: 1;
    backdrop-filter: blur(8px);
  }
}

/* Enhance button transitions */
button {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

button:hover {
  transform: translateY(-2px);
}

button:active {
  transform: translateY(1px);
}
</style>
